import type { MiceProjectView, MiceTraveler } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { useSnackbar } from '@web/ui/core';
import { useMiceOrderVouchers } from '../hooks/use_mice_orders';
import { useMiceTraveler } from '../hooks/use_mice_travelers';
import type { useTravelerActions } from '../hooks/use_traveler_actions';
import type { TravelerOverlayState } from '../hooks/use_traveler_overlays';
import { travelerProfileCheck } from '../travelers/eligibility';
import { CostCenterSection } from './cost_center_section';
import { eventDaysOf } from './need_dates';
import { NeedDrawer } from './need_drawer';
import { OrderDrawer } from './order_drawer';
import { ProfileSection } from './profile_section';
import { PurchaseReviewDialog } from './purchase_review_dialog';
import { VerifyPurchaseDialog } from './verify_purchase_dialog';

interface TravelerOverlaysProps {
  projectId: string;
  canPurchase: boolean;
  /** `mice:manage`: trocar o centro de custo do viajante (a permissão da rota que grava). */
  canManage: boolean;
  /** O projeto: as datas do evento (janela da troca de data). */
  project: MiceProjectView | undefined;
  /** Compras do evento abertas: encerradas, saem escolher outro voo, trocar data e pedir revisão
   *  (liberar nem abre: o "Conferir" da linha sai desligado). */
  purchasesOpen: boolean;
  /** Tolerância de preço do projeto: o teto que a revisão da compra mostra. */
  priceTolerance: number;
  travelers: readonly MiceTraveler[];
  /** Viajantes com necessidade, na ordem da lista filtrada: o painel navega por eles. */
  sequence: readonly MiceTraveler[];
  overlays: TravelerOverlayState;
  actions: ReturnType<typeof useTravelerActions>;
  /** Depois de comprar ou de uma ação em lote: limpa a seleção da lista. */
  onDone: () => void;
}

/** Pedido cujo voucher está aberto: o do viajante de planilha, ou o nó escolhido na árvore. */
function orderToShow(
  travelers: readonly MiceTraveler[],
  open: MiceTraveler | null,
  orderId: string | null,
) {
  if (open && !open.need) return open.orders[0]?.order ?? null;
  return travelers.flatMap((tr) => tr.orders).find((o) => o.order.id === orderId)?.order ?? null;
}

/**
 * As seções do viajante aberto: Cadastro (quando ele tem a checagem) e Centro de custo (sempre), no
 * painel da necessidade ou no do pedido de planilha solto.
 */
function sectionsOf(
  props: Readonly<TravelerOverlaysProps>,
  open: MiceTraveler | null,
): { need?: React.ReactNode; order?: React.ReactNode } {
  if (!open) return {};
  const check = travelerProfileCheck(open);
  const section = (
    <>
      {check ? (
        <ProfileSection
          // Prefixo: no painel ela é irmã da reconsideração, que usa o id da necessidade como key.
          key={`profile:${open.id}`}
          projectId={props.projectId}
          travelerId={open.id}
          travelerEmail={open.email}
          check={check}
          canResolve={props.canPurchase}
          focus={props.overlays.profileFocus}
        />
      ) : null}
      <CostCenterSection
        key={`cost-center:${open.id}`}
        projectId={props.projectId}
        travelerEmail={open.email}
        canChange={props.canManage}
      />
    </>
  );
  return open.need ? { need: section } : { order: section };
}

/** O viajante aberto: o da lista; senão o que a revisão da compra mandou completar, que o filtro
 *  da lista pode esconder (a revisão leva os aprovados do evento inteiro). */
function useOpenTraveler(
  projectId: string,
  travelers: readonly MiceTraveler[],
  overlays: TravelerOverlayState,
): MiceTraveler | null {
  const listed = travelers.find((tr) => tr.id === overlays.travelerId) ?? null;
  const outside =
    !listed && overlays.profileNeed?.id === overlays.travelerId ? overlays.profileNeed : null;
  const fetched = useMiceTraveler(projectId, outside);
  return listed ?? fetched;
}

export function TravelerOverlays(props: Readonly<TravelerOverlaysProps>) {
  const { overlays, actions, travelers, sequence } = props;
  const { showSnackbar } = useSnackbar();
  const open = useOpenTraveler(props.projectId, travelers, overlays);
  const openOrder = orderToShow(travelers, open, overlays.orderId);
  const vouchers = useMiceOrderVouchers(props.projectId, openOrder?.id ?? null);
  const index = open ? sequence.findIndex((tr) => tr.id === open.id) : -1;
  const step = (delta: -1 | 1): void => {
    const next = sequence[index + delta];
    if (next) overlays.openTraveler(next.id);
  };
  const verifying = travelers.find((tr) => tr.id === overlays.verifyId) ?? null;
  const verifyOrder =
    verifying?.orders.find((o) => o.order.status === 'indeterminate')?.order ?? null;
  const profile = sectionsOf(props, open);

  return (
    <>
      <PurchaseReviewDialog
        needs={overlays.purchase}
        priceTolerance={props.priceTolerance}
        onCancel={overlays.closePurchase}
        onCompleteProfile={(need) => {
          overlays.closePurchase();
          overlays.openProfile(need.id, need);
        }}
        onConfirm={(ids, skip) => {
          overlays.closePurchase();
          actions.order(ids, skip, props.onDone);
        }}
      />
      <VerifyPurchaseDialog
        traveler={verifying}
        order={verifyOrder}
        onRelease={(id) => {
          overlays.closeVerify();
          actions.release([id]);
        }}
        onWait={() => {
          overlays.closeVerify();
          showSnackbar({ severity: 'info', message: t('mice.verify.waiting') });
        }}
        onClose={overlays.closeVerify}
      />
      <NeedDrawer
        projectId={props.projectId}
        event={eventDaysOf(props.project)}
        need={open?.need ?? null}
        profile={profile.need}
        canPurchase={props.canPurchase}
        purchasesOpen={props.purchasesOpen}
        reconsidering={actions.reconsidering}
        onReconsider={actions.reconsider}
        choosing={actions.choosing}
        onChangeDate={actions.changeDate}
        changingDate={actions.changingDate}
        onCorrectEmail={(id, email, onDone) => {
          actions.correctEmail({ kind: 'need', id }, email, onDone);
        }}
        correctingEmail={actions.correctingEmail}
        onClose={overlays.closeDrawers}
        position={index >= 0 ? { index, total: sequence.length } : undefined}
        onStep={step}
        // "Marcar para emitir" (aprovar, ou trocar e aprovar) leva ao próximo da lista.
        onApprove={(needId) => {
          actions.approve([needId]);
          step(1);
        }}
        onChoose={(needId, choice) => {
          actions.choose(needId, choice);
          step(1);
        }}
      />
      <OrderDrawer
        order={openOrder}
        profile={profile.order}
        vouchers={vouchers.data?.items ?? []}
        otherLive={vouchers.data?.otherLive ?? []}
        loading={vouchers.isLoading}
        error={vouchers.isError}
        canPurchase={props.canPurchase}
        onCorrectEmail={(id, email, onDone) => {
          actions.correctEmail({ kind: 'order', id }, email, onDone);
        }}
        correctingEmail={actions.correctingEmail}
        onClose={overlays.closeDrawers}
      />
    </>
  );
}
